import { useState } from "react";
import { useIncidentReportPdfUrl } from "@/api/queries/incidents";
import { safeReportUrl } from "@/lib/reports";

/**
 * The one way a worker-rendered report PDF is handed to the user.
 *
 * Wraps the presign mutation and the anchor-navigation mechanics shared by the
 * report header and the editor row (RES-226 review §4): the API presigns the
 * S3 key with `Content-Disposition: attachment`, and navigating an anchor to
 * it makes the browser save the file. No blob fetch — the reports bucket's
 * CORS would block it — and no `download` attribute, which is ignored
 * cross-origin.
 *
 * `isError` covers BOTH failure modes: a failed presign (expired session, S3
 * error, key committed but object gone) and a presigned URL that fails
 * `safeReportUrl`. The second used to be silently swallowed — the button
 * animated "Preparing…" and then nothing happened. Every caller must render
 * an error line off this flag; the mutation is per-component, so one
 * caller's error surface cannot cover another's.
 */
export function useDownloadReportPdf(incidentId: string) {
  const pdf = useIncidentReportPdfUrl(incidentId);
  const [urlRejected, setUrlRejected] = useState(false);

  const download = () => {
    setUrlRejected(false);
    pdf.mutate(
      { download: true },
      {
        onSuccess: (res) => {
          const safeUrl = safeReportUrl(res.url);
          if (!safeUrl) {
            setUrlRejected(true);
            return;
          }
          const a = document.createElement("a");
          a.href = safeUrl;
          document.body.appendChild(a);
          a.click();
          a.remove();
        },
      },
    );
  };

  return {
    download,
    isPending: pdf.isPending,
    isError: pdf.isError || urlRejected,
  };
}
